import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useChildMonthlyTrend } from '@/hooks/useDailyUpdates';
import { cn } from '@/lib/utils';

// Default criteria schema (fallback)
const DEFAULT_CRITERIA_SCHEMA = [
  { key: 'mood', label: 'Nastrój', icon: '😊' },
  { key: 'eating', label: 'Jedzenie', icon: '🍽️' },
  { key: 'nap', label: 'Sen', icon: '😴' }
];

export default function MonthlySummary({ childId, criteriaSchema }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth() + 1; // 1-indexed

  const { current, previous, isLoading } = useChildMonthlyTrend(childId, year, month);

  const schema = criteriaSchema || DEFAULT_CRITERIA_SCHEMA;

  // Calculate stats for each criterion
  const stats = useMemo(() => {
    if (!current.data?.aggregated) return [];

    return schema.map(criterion => {
      const currentData = current.data.aggregated[criterion.key];
      const prevData = previous.data?.aggregated?.[criterion.key];

      if (!currentData || currentData.total === 0) {
        return {
          key: criterion.key,
          label: criterion.label,
          icon: criterion.icon,
          positivePercent: 0,
          dominantEmoji: '·',
          trend: null,
          hasData: false
        };
      }

      const positivePercent = Math.round((currentData.positive / currentData.total) * 100);

      // Determine dominant value
      let dominantEmoji = '😐';
      if (currentData.positive >= currentData.neutral && currentData.positive >= currentData.negative) {
        dominantEmoji = '😊';
      } else if (currentData.negative >= currentData.positive && currentData.negative >= currentData.neutral) {
        dominantEmoji = '😢';
      }

      // Calculate trend vs previous month
      let trend = null;
      if (prevData && prevData.total > 0) {
        const prevPositivePercent = (prevData.positive / prevData.total) * 100;
        const diff = positivePercent - prevPositivePercent;
        if (diff > 5) trend = 'up';
        else if (diff < -5) trend = 'down';
        else trend = 'stable';
      }

      return {
        key: criterion.key,
        label: criterion.label,
        icon: criterion.icon,
        positivePercent,
        dominantEmoji,
        trend,
        hasData: true
      };
    });
  }, [current.data, previous.data, schema]);

  const totalDays = current.data?.totalDays || 0;

  // Navigation
  const goToPrevMonth = () => {
    setSelectedDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
    const today = new Date();
    // Don't allow navigating to future months
    if (next <= today) {
      setSelectedDate(next);
    }
  };

  const isCurrentMonth = () => {
    const today = new Date();
    return selectedDate.getMonth() === today.getMonth() &&
           selectedDate.getFullYear() === today.getFullYear();
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <span className="text-green-500 text-xs">↑</span>;
    if (trend === 'down') return <span className="text-red-500 text-xs">↓</span>;
    if (trend === 'stable') return <span className="text-slate-400 text-xs">→</span>;
    return null;
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      {/* Header - always visible */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left hover:bg-slate-50 rounded-lg p-2 -m-2 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-500" />
          <span className="font-medium text-slate-700 text-sm">Podsumowanie miesięczne</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Month navigation */}
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={goToPrevMonth}
            >
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <span className="text-xs text-slate-500 w-28 text-center">
              {format(selectedDate, 'LLLL yyyy', { locale: pl })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={goToNextMonth}
              disabled={isCurrentMonth()}
            >
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-slate-400 transition-transform duration-300 ease-out",
              expanded && "rotate-180"
            )}
          />
        </div>
      </div>

      {/* Animated expandable content */}
      <div
        className={cn(
          "grid transition-all duration-300 ease-out",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-4 space-y-3">
          {isLoading ? (
            <div className="text-center py-4 text-slate-400 text-sm">
              Ładowanie...
            </div>
          ) : totalDays === 0 ? (
            <div className="text-center py-4 text-slate-400 text-sm">
              Brak ocen w tym miesiącu
            </div>
          ) : (
            <>
              {/* Criteria bars */}
              {stats.map(stat => (
                <div key={stat.key} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      {stat.icon} {stat.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-700 font-medium">
                        {stat.hasData ? `${stat.positivePercent}%` : '-'}
                      </span>
                      <span className="text-lg">{stat.dominantEmoji}</span>
                      {getTrendIcon(stat.trend)}
                    </div>
                  </div>
                  {stat.hasData && (
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-500",
                          stat.positivePercent >= 70 ? "bg-green-400" :
                          stat.positivePercent >= 40 ? "bg-yellow-400" : "bg-red-400"
                        )}
                        style={{ width: `${stat.positivePercent}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Footer */}
              <div className="pt-2 text-xs text-slate-400 text-center">
                Dni z oceną: {totalDays}
              </div>

              {/* Legend */}
              <div className="flex justify-center flex-wrap gap-3 text-xs text-slate-500 pt-2 border-t border-slate-100">
                <span>😊 Świetnie</span>
                <span>😐 OK</span>
                <span>😢 Trudniej</span>
                <span className="text-green-500">↑ Lepiej</span>
                <span className="text-slate-400">→ Stabilnie</span>
                <span className="text-red-500">↓ Słabiej</span>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
